// src/modules/notes.js � Notes list, view, modal, copy, tag edit, audio playback
import { state } from '../state.js';
import { getSessionToken, getAuthHeaders, apiFetch } from '../utils/api.js';
import { escapeHtml, escapeJsAttr, showView, showToast, showConfirmDialog, isValidUuid } from '../utils/dom.js';
import { formatTimeAgo } from '../utils/format.js';
import { APP_VERSION, API_BASE, PAGE_LOAD_TIME, LOAD_NOTES_DEBOUNCE_MS } from '../utils/constants.js';
import { logger, sanitizeLogs, addNetworkLog, apiFetchWithRefresh } from './debug.js';
import { AudioCache } from '../utils/storage.js';
import { ProfileStore } from '../stores/profile-store.js';
import { resetNoteFeedback, initNoteFeedback } from './analytics.js';
import { findConsentForNote } from './consent.js';
import { attachPatientPicker, linkNote, unlinkNote, bulkLinkNotes } from './patients.js';

// --- Notes Pagination State ---
let notesPageSize = 20;
let notesOffset = 0;
let notesTotalCount = 0;
let allLoadedNotes = [];
let _lastLoadNotesTime = 0;

// --- Bulk-link selection mode (B3) ---
let bulkSelectMode = false;
const bulkSelectedIds = new Set(); // ticked noteIds (survives local re-render)
let bulkPatientPicker = null;      // attachPatientPicker controller, created lazily

/** Getter for allLoadedNotes — used by upload.js checkDuplicateTag */
export function getAllLoadedNotes() { return allLoadedNotes; }

// --- Audio Playback State ---
let noteAudioPlayer = null;
let noteAudioObjectUrl = null;
let noteAudioAnimFrame = null;

export async function loadNotes(reset = false) {
    // BUG 4 FIX: skip if same request was made within debounce window (unless forced reset)
    const now = Date.now();
    if (!reset && (now - _lastLoadNotesTime) < LOAD_NOTES_DEBOUNCE_MS && allLoadedNotes.length > 0) {
        return;
    }
    _lastLoadNotesTime = now;
    const container = document.getElementById('notesList');
    const loadMoreContainer = document.getElementById('notesLoadMore');
    const countDisplay = document.getElementById('notesCount');

    if (reset) {
        notesOffset = 0;
        allLoadedNotes = [];
        // Show skeleton loading placeholders
        container.innerHTML = Array(3).fill('<div class="note-card-skeleton"><div class="skeleton-line skeleton-date"></div><div class="skeleton-line skeleton-label"></div><div class="skeleton-line skeleton-status"></div></div>').join('');
    }

    try {
        // Build the API path with pagination params
        let notesPath = '/notes?limit=' + notesPageSize + '&offset=' + notesOffset;
        if (ProfileStore.get('currentProfileId')) {
            notesPath += '&profileId=' + encodeURIComponent(ProfileStore.get('currentProfileId'));
        }
        const res = await apiFetch(notesPath);
        const data = await res.json();

        if (!data.notes || (data.notes.length === 0 && notesOffset === 0)) {
            container.innerHTML = '<div class="empty-state"><p>No notes yet.</p></div>';
            loadMoreContainer.classList.add('hidden');
            countDisplay.textContent = '';
            return;
        }

        // Append new notes to our local array
        allLoadedNotes = reset ? data.notes : allLoadedNotes.concat(data.notes);
        notesTotalCount = data.pagination ? data.pagination.total : allLoadedNotes.length;

        // Apply local date filter
        const filtered = applyDateFilter(allLoadedNotes);

        // Render
        renderNotesList(filtered);

        // BUG 3 FIX: Update count display — "of X" reflects filtered total, not absolute total
        const showingCount = filtered.length;
        const dateFilter = document.getElementById('notesDateFilter').value;
        const displayTotal = (dateFilter === 'all') ? notesTotalCount : filtered.length;
        countDisplay.textContent = `Showing ${showingCount} of ${displayTotal} notes`;

        // BUG 5 FIX: Show/hide Load More — hide when all notes are loaded OR filtered shows all
        const hasMore = data.pagination && data.pagination.hasMore;
        if (hasMore && dateFilter === 'all') {
            loadMoreContainer.classList.remove('hidden');
        } else {
            loadMoreContainer.classList.add('hidden');
        }

        // Update offset for next page
        if (data.pagination && data.pagination.nextOffset !== null) {
            notesOffset = data.pagination.nextOffset;
        }
    } catch (err) {
        logger.error('SYSTEM', 'Failed to load notes', err);
        container.innerHTML = '<div class="empty-state"><p>Error: ' + escapeHtml(err.message) + '</p></div>';
    }
}

export function loadMoreNotes() {
    loadNotes(false);
}

export function applyDateFilter(notes) {
    const filter = document.getElementById('notesDateFilter').value;
    if (filter === 'all') return notes;

    const now = new Date();
    let cutoff;
    if (filter === 'today') {
        cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    } else if (filter === 'week') {
        cutoff = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (filter === 'month') {
        cutoff = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    } else {
        return notes;
    }

    return notes.filter(note => {
        const noteDate = new Date(note.lastModified);
        return noteDate >= cutoff;
    });
}

export function filterNotesLocally() {
    const searchTerm = document.getElementById('notesSearchInput').value.trim().toLowerCase();
    let filtered = applyDateFilter(allLoadedNotes);

    if (searchTerm) {
        filtered = filtered.filter(note => {
            const label = (note.patientLabel || '').toLowerCase();
            return label.includes(searchTerm);
        });
    }

    renderNotesList(filtered);
    const countDisplay = document.getElementById('notesCount');
    // BUG 3 FIX: show filtered total, not absolute total
    const dateFilter = document.getElementById('notesDateFilter').value;
    const displayTotal = (dateFilter === 'all' && !searchTerm) ? notesTotalCount : filtered.length;
    countDisplay.textContent = `Showing ${filtered.length} of ${displayTotal} notes`;

    // BUG 5 FIX: hide Load More when filtering locally
    const loadMoreContainer = document.getElementById('notesLoadMore');
    if (searchTerm || dateFilter !== 'all' || allLoadedNotes.length >= notesTotalCount) {
        loadMoreContainer.classList.add('hidden');
    }
}

export function renderNotesList(notes) {
    const container = document.getElementById('notesList');

    if (!notes || notes.length === 0) {
        container.innerHTML = '<div class="empty-state"><p>No notes match your filters.</p></div>';
        return;
    }

    notes.sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified));
    container.innerHTML = notes.map(note => {
        const date = new Date(note.lastModified).toLocaleString();
        const label = note.patientLabel || note.noteId.substring(0, 8) + '...';
        const escapedLabel = escapeJsAttr(label);
        const labelHtml = escapeHtml(label);
        const timeSaved = note.estimatedTimeSavedMinutes ? Math.round(note.estimatedTimeSavedMinutes) : 0;
        const timeSavedHtml = timeSaved > 0 ? `<span class="time-saved-badge">&#9201; ${timeSaved} min saved</span>` : '';
        // Audio duration badge
        const audioDuration = note.audioDurationSeconds ? Math.round(note.audioDurationSeconds) : 0;
        let audioDurationHtml = '';
        if (audioDuration > 0) {
            const mins = Math.floor(audioDuration / 60);
            const secs = audioDuration % 60;
            const durationStr = mins > 0 ? mins + 'm ' + secs + 's' : secs + 's';
            audioDurationHtml = `<span class="audio-duration-badge">&#127908; ${durationStr}</span>`;
        }
        // Quality score badge
        let qualityHtml = '';
        if (note.soapCompletenessScore != null && note.soapCompletenessScore > 0) {
            const score = note.soapCompletenessScore;
            const qualityClass = score >= 90 ? 'quality-excellent' : score >= 70 ? 'quality-good' : 'quality-fair';
            qualityHtml = `<span class="quality-badge ${qualityClass}">${score}%</span>`;
        }
        // Review required indicator
        const reviewHtml = note.reviewRequired ? '<span class="review-badge">&#9888; Review</span>' : '';
        // Appointment type badge (skip for other_general — too noisy)
        let apptBadgeHtml = '';
        const apptType = note.appointmentType;
        if (apptType) {
            const typeInfo = state.APPOINTMENT_TYPES.find(function(t) { return t.key === apptType; });
            if (typeInfo) {
                const categoryClass = 'appt-badge-' + (typeInfo.category || 'General').toLowerCase();
                apptBadgeHtml = `<span class="note-appt-badge ${categoryClass}">${escapeHtml(typeInfo.shortLabel)}</span>`;
            }
        }
        // Quick Note category badge
        let noteCategoryBadge = '';
        if (note.recordingMode === 'quick_note' && note.noteCategory) {
            const categoryLabels = {
                procedure_note: 'Procedure Note',
                progress_note: 'Progress Note',
                consult_note: 'Consult Note',
                focused_assessment: 'Focused Assessment',
                full_soap: 'SOAP (Dictation)',
            };
            const catLabel = categoryLabels[note.noteCategory] || note.noteCategory;
            noteCategoryBadge = ` <span class="note-appt-badge appt-badge-general">${escapeHtml(catLabel)}</span>`;
        }
        // Bulk-link selection mode: tick instead of open; keep label+date visible (verify-before-tick).
        const linkedBadge = note.patientId ? '<span class="badge bg-success ms-1">linked</span>' : '';
        const cardAction = bulkSelectMode
            ? `data-action="toggle-note-select" data-id="${note.noteId}"`
            : `data-action="view-note" data-id="${note.noteId}"`;
        let checkboxHtml = '';
        if (bulkSelectMode) {
            const checked = bulkSelectedIds.has(note.noteId) ? ' checked' : '';
            checkboxHtml = `<div class="form-check d-flex align-items-center me-2" style="min-height:44px">
                <input class="form-check-input" type="checkbox" data-action="toggle-note-select" data-id="${note.noteId}" data-stop aria-label="Select note"${checked} style="width:1.4em;height:1.4em">
            </div>`;
        }
        return `<div class="note-card${bulkSelectMode ? ' d-flex align-items-center' : ''}" ${cardAction}>
            ${checkboxHtml}
            <div class="note-card-left">
                <div class="note-date">${date}</div>
                <div class="note-label-row">
                    <span class="note-label" id="label-${note.noteId}">${labelHtml}</span>
                    ${linkedBadge}
                    ${apptBadgeHtml}${noteCategoryBadge}
                    ${reviewHtml}
                    <button class="btn-edit-tag" data-action="start-edit-tag" data-id="${note.noteId}" data-label="${escapedLabel}" data-stop title="Edit tag">&#9998;</button>
                </div>
                <div class="edit-tag-inline hidden" id="edit-${note.noteId}">
                    <input type="text" class="edit-tag-input" id="input-${note.noteId}" value="${labelHtml}" data-stop>
                    <button class="btn-edit-save" data-action="save-edit-tag" data-id="${note.noteId}" data-stop>&#10003;</button>
                    <button class="btn-edit-cancel" data-action="cancel-edit-tag" data-id="${note.noteId}" data-label="${escapedLabel}" data-stop>&#10005;</button>
                </div>
                <span class="note-status completed">Completed</span>
            </div>
            <div class="note-card-right">
                ${audioDurationHtml}
                ${timeSavedHtml}
                ${qualityHtml}
                ${audioDuration > 0 ? `<button class="note-play-btn btn btn-sm btn-outline-primary" data-note-id="${note.noteId}" data-action="play-note-audio" data-id="${note.noteId}" data-stop title="Play audio">&#128266;</button>` : ''}
            </div>
        </div>`;
    }).join('');
}

// --- Edit Tag (inline on notes list) ---
export function startEditTag(noteId, currentLabel) {
    document.getElementById('label-' + noteId).parentElement.classList.add('hidden');
    const editContainer = document.getElementById('edit-' + noteId);
    editContainer.classList.remove('hidden');
    const input = document.getElementById('input-' + noteId);
    input.value = currentLabel;
    input.focus();
    input.select();
}

export function cancelEditTag(noteId, originalLabel) {
    document.getElementById('edit-' + noteId).classList.add('hidden');
    document.getElementById('label-' + noteId).parentElement.classList.remove('hidden');
}

export async function saveEditTag(noteId) {
    const input = document.getElementById('input-' + noteId);
    const newLabel = input.value.trim();
    if (!newLabel) { input.focus(); return; }

    input.disabled = true;
    try {
        const res = await apiFetch('/notes/' + noteId, {
            method: 'PATCH',
            body: JSON.stringify({ patientLabel: newLabel }),
        });
        if (!res.ok) throw new Error('Failed to update tag');
        loadNotes(true); // Refresh the list
    } catch (err) {
        showToast('Error updating tag: ' + err.message, 'error');
        input.disabled = false;
    }
}

/**
 * Convert markdown-formatted SOAP note to safe HTML for display.
 * Escapes HTML first (XSS prevention), then applies formatting.
 */
export function formatNoteAsHtml(text) {
    let html = escapeHtml(text);

    // Process line by line for headings and bullets
    html = html.split('\n').map(line => {
        // ## Heading (h4)
        if (/^##\s+(.+)$/.test(line)) {
            return '<h4 class="soap-heading">' + line.replace(/^##\s+/, '') + '</h4>';
        }
        // # Heading (h3) — but NOT tooth numbers like #10, #14
        if (/^#\s+(.+)$/.test(line)) {
            return '<h3 class="soap-heading">' + line.replace(/^#\s+/, '') + '</h3>';
        }
        // * bullet item (including indented)
        if (/^(\s*)\*\s+(.+)$/.test(line)) {
            const match = line.match(/^(\s*)\*\s+(.+)$/);
            const indent = match[1].length > 0 ? ' style="margin-left:' + (match[1].length * 6) + 'px"' : '';
            return '<div class="soap-bullet"' + indent + '>' + match[2] + '</div>';
        }
        // - dash bullet item (including indented)
        if (/^(\s*)-\s+(.+)$/.test(line)) {
            const match = line.match(/^(\s*)-\s+(.+)$/);
            const indent = match[1].length > 0 ? ' style="margin-left:' + (match[1].length * 6) + 'px"' : '';
            return '<div class="soap-bullet soap-dash"' + indent + '>' + match[2] + '</div>';
        }
        return line;
    }).join('\n');

    // Bold: **text** → <strong>text</strong>
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Wrap remaining plain lines in spans for consistent spacing
    // (lines that aren't already wrapped in tags)
    html = html.split('\n').map(line => {
        if (line.startsWith('<h3') || line.startsWith('<h4') || line.startsWith('<div class="soap-bullet')) {
            return line;
        }
        return '<span class="soap-line">' + line + '</span>';
    }).join('\n');

    return html;
}

/**
 * Strip markdown formatting for clean plain-text copy (Open Dental / Eaglesoft compatible).
 * Preserves tooth numbers (#10, #14, #3) but removes heading markers.
 */
export function stripNoteForCopy(text) {
    return text.split('\n').map(line => {
        // Remove ## heading markers
        if (/^##\s+(.+)$/.test(line)) {
            return line.replace(/^##\s+/, '').toUpperCase();
        }
        // Remove # heading markers (but NOT tooth numbers like #10)
        if (/^#\s+(.+)$/.test(line)) {
            return line.replace(/^#\s+/, '').toUpperCase();
        }
        // Remove * bullet markers (including indented), replace with -
        if (/^(\s*)\*\s+(.+)$/.test(line)) {
            const match = line.match(/^(\s*)\*\s+(.+)$/);
            return match[1] + '- ' + match[2];
        }
        return line;
    }).join('\n')
    // Remove **bold** markers
    .replace(/\*\*(.+?)\*\*/g, '$1');
}

export async function viewNote(noteId) {
    try {
        if (!isValidUuid(noteId)) {
            showToast('Invalid note ID.', 'error');
            return;
        }
        const res = await apiFetchWithRefresh('/notes/' + noteId);
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to load note');
        }
        const data = await res.json();
        state.currentNoteContent = data.editedNote || data.soapNote || 'No content';
        const noteLabel = data.patientLabel || noteId.substring(0, 8) + '...';

        document.getElementById('modalTitle').textContent = 'SOAP Note';

        // Build tag display with edit capability
        const tagContainer = document.getElementById('modalTagContainer');

        // Appointment type badge for modal
        let modalApptBadge = '';
        const noteApptType = data.appointmentType;
        if (noteApptType) {
            const typeInfo = state.APPOINTMENT_TYPES.find(function(t) { return t.key === noteApptType; });
            if (typeInfo) {
                const categoryClass = 'appt-badge-' + (typeInfo.category || 'General').toLowerCase();
                modalApptBadge = `<span class="note-appt-badge modal-appt-badge ${categoryClass}">${escapeHtml(typeInfo.shortLabel)}</span>`;
            }
        }
        // Quick Note category badge for modal
        if (data.recordingMode === 'quick_note' && data.noteCategory) {
            const categoryLabels = {
                procedure_note: 'Procedure Note',
                progress_note: 'Progress Note',
                consult_note: 'Consult Note',
                focused_assessment: 'Focused Assessment',
                full_soap: 'SOAP (Dictation)',
            };
            const catLabel = categoryLabels[data.noteCategory] || data.noteCategory;
            modalApptBadge += ` <span class="note-appt-badge modal-appt-badge appt-badge-general">${escapeHtml(catLabel)}</span>`;
        }

        tagContainer.innerHTML = `
            <div class="modal-tag-display" id="modalTagDisplay">
                <span class="modal-tag-label">Tag:</span>
                <span class="modal-tag-value" id="modalTagValue">${escapeHtml(noteLabel)}</span>
                ${modalApptBadge}
                <button class="btn-edit-tag" data-action="start-modal-tag-edit" data-id="${noteId}" title="Edit tag">&#9998;</button>
            </div>
            <div class="modal-tag-edit hidden" id="modalTagEdit">
                <input type="text" class="edit-tag-input" id="modalTagInput" value="${escapeHtml(noteLabel).replace(/"/g, '&quot;')}">
                <button class="btn-edit-save" data-action="save-modal-tag" data-id="${noteId}">&#10003;</button>
                <button class="btn-edit-cancel" data-action="cancel-modal-tag-edit">&#10005;</button>
            </div>
            ${data.estimatedTimeSavedMinutes ? `<div class="note-time-saved">&#9201; Saved ~${Math.round(data.estimatedTimeSavedMinutes)} minutes of documentation time</div>` : ''}
        `;

        document.getElementById('modalContent').innerHTML = formatNoteAsHtml(state.currentNoteContent);

        // Edit note affordance — mirror of the tag-edit pattern (view <-> edit swap)
        const noteEditBar = document.getElementById('modalContentEditBar');
        if (noteEditBar) {
            noteEditBar.innerHTML = `<button class="btn btn-sm btn-outline-secondary note-edit-btn" data-action="start-note-edit" data-id="${noteId}">&#9998; Edit note</button>`;
        }
        const noteEditPanel = document.getElementById('modalContentEdit');
        if (noteEditPanel) {
            noteEditPanel.classList.add('hidden');
            noteEditPanel.innerHTML = '';
        }

        // Audio play button in modal (only if note has audio duration)
        const modalAudioSection = document.getElementById('modalAudioSection');
        if (modalAudioSection) {
            if (data.audioDurationSeconds && data.audioDurationSeconds > 0) {
                modalAudioSection.innerHTML = `<button class="note-play-btn btn btn-sm btn-outline-primary" id="modalPlayBtn" data-note-id="${noteId}" title="Play audio">&#128266; Play</button>`;
                document.getElementById('modalPlayBtn').addEventListener('click', function() {
                    playNoteAudio(noteId, this);
                });
                modalAudioSection.classList.remove('hidden');
            } else {
                modalAudioSection.innerHTML = '';
                modalAudioSection.classList.add('hidden');
            }
        }

        // Transcript section — on-demand via button
        const transcriptContainer = document.getElementById('modalTranscriptContainer');
        const transcriptBtn = document.getElementById('showTranscriptBtn');
        if (transcriptContainer) {
            transcriptContainer.innerHTML = '';
            transcriptContainer.classList.remove('show');
            transcriptContainer.dataset.noteId = '';
        }
        if (transcriptBtn) {
            transcriptBtn.innerHTML = '&#128196; Show Transcript';
            transcriptBtn.disabled = false;
            transcriptBtn.dataset.noteId = noteId;
            transcriptBtn.onclick = function() { showNoteTranscript(noteId); };
        }

        // Referral panel — bind to this note and reset to collapsed/empty state
        referralNoteId = noteId;
        resetReferralPanel();

        // Link-to-patient panel — bind to this note, reset, then reflect current link state
        linkNoteId = noteId;
        resetLinkPatientPanel();
        initLinkPatientState(data);

        document.getElementById('noteModal').classList.add('active');
        // Show/hide consent PDF download button
        const consentPdfBtn = document.getElementById('downloadConsentPdfBtn');
        if (consentPdfBtn) {
            // Always set data attributes (download function handles server fallback)
            consentPdfBtn.dataset.patientLabel = noteLabel;
            consentPdfBtn.dataset.noteDate = data.createdAt || '';
            consentPdfBtn.dataset.noteId = noteId;
            // Show if localStorage has consent OR patientLabel has first+last name (server may have it)
            const consent = findConsentForNote(noteLabel, data.createdAt || '');
            const parts = noteLabel.trim().split(/\s+/);
            if (consent || parts.length >= 2) {
                consentPdfBtn.classList.remove('d-none');
            } else {
                consentPdfBtn.classList.add('d-none');
            }
        }
        // Initialize feedback buttons for this note
        initNoteFeedback(noteId, data.feedbackRating || null, data.feedbackText || '');
    } catch (err) { showToast('Error: ' + err.message, 'error'); }
}

// --- Edit Tag in Modal ---
export function startModalTagEdit(noteId) {
    document.getElementById('modalTagDisplay').classList.add('hidden');
    document.getElementById('modalTagEdit').classList.remove('hidden');
    const input = document.getElementById('modalTagInput');
    input.focus();
    input.select();
}

export function cancelModalTagEdit() {
    document.getElementById('modalTagEdit').classList.add('hidden');
    document.getElementById('modalTagDisplay').classList.remove('hidden');
}

export async function saveModalTag(noteId) {
    const input = document.getElementById('modalTagInput');
    const newLabel = input.value.trim();
    if (!newLabel) { input.focus(); return; }

    input.disabled = true;
    try {
        const res = await apiFetch('/notes/' + noteId, {
            method: 'PATCH',
            body: JSON.stringify({ patientLabel: newLabel }),
        });
        if (!res.ok) throw new Error('Failed to update tag');
        // Update the display in modal
        document.getElementById('modalTagValue').textContent = newLabel;
        cancelModalTagEdit();
        input.disabled = false;
        // Also refresh notes list in background
        loadNotes();
    } catch (err) {
        showToast('Error updating tag: ' + err.message, 'error');
        input.disabled = false;
    }
}

// --- Edit Note Body in Modal ---
// Swaps the rendered #modalContent for a textarea holding the RAW markdown.
// Keeps both versions server-side; PATCH { editedNote } persists the edit.
export function startNoteEdit(noteId) {
    document.getElementById('modalContent').classList.add('hidden');
    document.getElementById('modalContentEditBar').classList.add('hidden');
    const panel = document.getElementById('modalContentEdit');
    panel.classList.remove('hidden');
    panel.innerHTML = `
        <textarea class="form-control note-edit-textarea" id="noteEditTextarea" rows="14"></textarea>
        <div class="note-edit-actions mt-2">
            <button class="btn btn-sm btn-primary" data-action="save-note-edit" data-id="${noteId}">Save</button>
            <button class="btn btn-sm btn-outline-secondary" data-action="cancel-note-edit">Cancel</button>
        </div>`;
    const ta = document.getElementById('noteEditTextarea');
    ta.value = state.currentNoteContent;
    ta.focus();
}

export function cancelNoteEdit() {
    const panel = document.getElementById('modalContentEdit');
    if (!panel || panel.classList.contains('hidden')) return; // safe when not in edit mode
    panel.classList.add('hidden');
    panel.innerHTML = '';
    document.getElementById('modalContent').classList.remove('hidden');
    document.getElementById('modalContentEditBar').classList.remove('hidden');
}

export async function saveNoteEdit(noteId) {
    const ta = document.getElementById('noteEditTextarea');
    const edited = ta.value;
    if (!edited.trim()) { showToast('Note cannot be empty.', 'error'); ta.focus(); return; }

    const saveBtn = document.querySelector('#modalContentEdit [data-action="save-note-edit"]');
    if (saveBtn) saveBtn.disabled = true;
    ta.disabled = true;
    try {
        const res = await apiFetch('/notes/' + noteId, {
            method: 'PATCH',
            body: JSON.stringify({ editedNote: edited }),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to update note');
        }
        const data = await res.json();
        // Server echoes the saved text — trust it as the new latest version
        state.currentNoteContent = data.editedNote != null ? data.editedNote : edited;
        document.getElementById('modalContent').innerHTML = formatNoteAsHtml(state.currentNoteContent);
        cancelNoteEdit();
        showToast('Note saved.', 'success');
    } catch (err) {
        showToast('Error saving note: ' + err.message, 'error');
        ta.disabled = false;
        if (saveBtn) saveBtn.disabled = false;
    }
}

export function closeModal() { stopNoteAudio(); cancelNoteEdit(); resetReferralPanel(); resetLinkPatientPanel(); document.getElementById('noteModal').classList.remove('active'); resetNoteFeedback(); }

// --- Referral letter (Feature 2) ---
// POST /notes/{noteId}/referral -> { referralLetter, specialty }.
// The letter text and note text are PHI — never logged.

/** Current note id for the referral panel — set when the modal opens. */
let referralNoteId = null;

/** Reset the referral panel to its collapsed, empty state (on modal open/close). */
export function resetReferralPanel() {
    const panel = document.getElementById('referralPanel');
    const toggleBtn = document.getElementById('referralToggleBtn');
    const status = document.getElementById('referralStatus');
    const result = document.getElementById('referralResult');
    const letter = document.getElementById('referralLetterText');
    const specialty = document.getElementById('referralSpecialty');
    const reason = document.getElementById('referralReason');
    const genBtn = document.getElementById('referralGenerateBtn');
    if (panel) panel.classList.add('hidden');
    if (toggleBtn) { toggleBtn.setAttribute('aria-expanded', 'false'); }
    if (status) { status.classList.add('hidden'); status.innerHTML = ''; }
    if (result) result.classList.add('hidden');
    if (letter) letter.value = '';
    if (specialty) specialty.selectedIndex = 0;
    if (reason) reason.value = '';
    if (genBtn) { genBtn.disabled = false; genBtn.innerHTML = 'Generate letter'; }
}

export function toggleReferralPanel() {
    const panel = document.getElementById('referralPanel');
    const toggleBtn = document.getElementById('referralToggleBtn');
    if (!panel) return;
    const willShow = panel.classList.contains('hidden');
    panel.classList.toggle('hidden', !willShow);
    if (toggleBtn) toggleBtn.setAttribute('aria-expanded', willShow ? 'true' : 'false');
}

export async function generateReferral() {
    const noteId = referralNoteId;
    const status = document.getElementById('referralStatus');
    const result = document.getElementById('referralResult');
    const letter = document.getElementById('referralLetterText');
    const genBtn = document.getElementById('referralGenerateBtn');
    const specialtyEl = document.getElementById('referralSpecialty');
    const reasonEl = document.getElementById('referralReason');
    if (!status || !result || !letter || !genBtn || !specialtyEl) return;

    if (!isValidUuid(noteId)) {
        status.classList.remove('hidden');
        status.innerHTML = '<span class="text-danger">Invalid note — cannot generate referral.</span>';
        return;
    }

    const specialty = specialtyEl.value;
    const reason = (reasonEl ? reasonEl.value : '').trim();
    const body = { specialty: specialty };
    if (reason) body.reason = reason;

    // Loading state
    genBtn.disabled = true;
    genBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Generating...';
    result.classList.add('hidden');
    status.classList.remove('hidden');
    status.innerHTML = '<span class="text-muted"><span class="spinner-border spinner-border-sm" role="status"></span> Drafting referral letter...</span>';

    try {
        const res = await apiFetchWithRefresh('/notes/' + noteId + '/referral', {
            method: 'POST',
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            // Map backend errors to clear UI messages (never echo PHI).
            let msg;
            if (res.status === 404) {
                msg = 'Note not found, or it has no content to draft from.';
            } else if (res.status === 502) {
                msg = 'Could not generate the referral letter. Please try again.';
            } else if (res.status === 400) {
                msg = err.error || 'Please pick a valid specialty.';
            } else {
                msg = err.error || 'Failed to generate referral letter.';
            }
            throw new Error(msg);
        }
        const data = await res.json();
        if (!data.referralLetter) throw new Error('No letter was returned. Please try again.');

        letter.value = data.referralLetter;
        status.classList.add('hidden');
        status.innerHTML = '';
        result.classList.remove('hidden');
        logger.info('NOTES', 'Referral letter generated', { noteId: noteId, specialty: specialty });
    } catch (err) {
        result.classList.add('hidden');
        status.classList.remove('hidden');
        status.innerHTML = '<span class="text-danger">' + escapeHtml(err.message) + '</span>';
    } finally {
        genBtn.disabled = false;
        genBtn.innerHTML = 'Generate letter';
    }
}

export async function copyReferralLetter() {
    const letter = document.getElementById('referralLetterText');
    const btn = document.getElementById('referralCopyBtn');
    if (!letter) return;
    const text = letter.value;
    try {
        await navigator.clipboard.writeText(text);
    } catch (err) {
        letter.select();
        document.execCommand('copy');
    }
    if (btn) {
        const original = btn.innerHTML;
        btn.innerHTML = '&#10003; Copied!';
        setTimeout(() => { btn.innerHTML = original; }, 2000);
    }
}

// --- Link to patient (FEAT-002) ---
// POST /notes/{noteId}/link links or unlinks the note. patientLabel is PHI — escapeHtml,
// never logged. The picker lives in #notePatientPicker inside the note modal.

/** Current note id for the link panel — set when the modal opens. */
let linkNoteId = null;
/** The modal picker controller, created lazily on first open. */
let notePatientPicker = null;

/** Reset the link-patient panel to its collapsed, empty state (on modal open/close). */
export function resetLinkPatientPanel() {
    const panel = document.getElementById('linkPatientPanel');
    const toggleBtn = document.getElementById('linkPatientToggleBtn');
    const current = document.getElementById('linkPatientCurrent');
    const status = document.getElementById('linkPatientStatus');
    const linkBtn = document.getElementById('linkPatientBtn');
    if (panel) panel.classList.add('hidden');
    if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
    if (current) { current.classList.add('d-none'); current.textContent = ''; }
    if (status) status.textContent = '';
    if (linkBtn) { linkBtn.disabled = true; linkBtn.innerHTML = 'Link'; }
    if (notePatientPicker) notePatientPicker.reset();
    updateUnlinkVisibility(false);
}

/** Set the "currently linked" indicator + Unlink availability from note detail. */
export function initLinkPatientState(data) {
    const current = document.getElementById('linkPatientCurrent');
    const linked = !!(data && data.patientId);
    if (current) {
        if (linked) {
            const label = data.patientLabel || 'linked patient';
            current.innerHTML = 'Linked to &#128100; ' + escapeHtml(label);
            current.classList.remove('d-none');
        } else {
            current.classList.add('d-none');
            current.textContent = '';
        }
    }
    updateUnlinkVisibility(linked);
}

function updateUnlinkVisibility(show) {
    const unlinkBtn = document.getElementById('unlinkPatientBtn');
    if (unlinkBtn) unlinkBtn.classList.toggle('d-none', !show);
}

export function toggleLinkPatientPanel() {
    const panel = document.getElementById('linkPatientPanel');
    const toggleBtn = document.getElementById('linkPatientToggleBtn');
    if (!panel) return;
    const willShow = panel.classList.contains('hidden');
    panel.classList.toggle('hidden', !willShow);
    if (toggleBtn) toggleBtn.setAttribute('aria-expanded', willShow ? 'true' : 'false');
    if (willShow && !notePatientPicker) {
        const host = document.getElementById('notePatientPicker');
        if (host) {
            notePatientPicker = attachPatientPicker(host, {
                onChange: (pid) => {
                    const linkBtn = document.getElementById('linkPatientBtn');
                    if (linkBtn) linkBtn.disabled = !pid;
                },
            });
        }
    }
}

export async function linkNoteToPatient() {
    const patientId = notePatientPicker ? notePatientPicker.getPatientId() : null;
    const status = document.getElementById('linkPatientStatus');
    const linkBtn = document.getElementById('linkPatientBtn');
    if (!patientId || !isValidUuid(linkNoteId)) return;

    if (linkBtn) { linkBtn.disabled = true; linkBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Linking...'; }
    if (status) { status.className = 'link-patient-status small ms-1 text-muted'; status.textContent = ''; }
    try {
        await linkNote(linkNoteId, patientId);
        if (status) { status.className = 'link-patient-status small ms-1 text-success'; status.textContent = 'Linked.'; }
        // Reflect the new linked state; refresh the list so counts/ordering update.
        const picked = document.querySelector('#notePatientPicker [data-patient-selected]');
        const label = picked ? picked.textContent.replace(/\u00d7\s*$/, '').trim() : '';
        initLinkPatientState({ patientId: patientId, patientLabel: label });
        if (notePatientPicker) notePatientPicker.reset();
        loadNotes();
        logger.info('NOTES', 'Note linked to patient', { noteId: linkNoteId, patientId: patientId });
    } catch (err) {
        if (status) { status.className = 'link-patient-status small ms-1 text-danger'; status.textContent = 'Could not link. Please try again.'; }
    } finally {
        if (linkBtn) linkBtn.innerHTML = 'Link';
    }
}

export async function unlinkNoteFromPatient() {
    const status = document.getElementById('linkPatientStatus');
    const unlinkBtn = document.getElementById('unlinkPatientBtn');
    if (!isValidUuid(linkNoteId)) return;

    if (unlinkBtn) { unlinkBtn.disabled = true; unlinkBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Unlinking...'; }
    if (status) { status.className = 'link-patient-status small ms-1 text-muted'; status.textContent = ''; }
    try {
        await unlinkNote(linkNoteId);
        if (status) { status.className = 'link-patient-status small ms-1 text-success'; status.textContent = 'Unlinked.'; }
        initLinkPatientState({ patientId: null });
        loadNotes();
        logger.info('NOTES', 'Note unlinked from patient', { noteId: linkNoteId });
    } catch (err) {
        if (status) { status.className = 'link-patient-status small ms-1 text-danger'; status.textContent = 'Could not unlink. Please try again.'; }
    } finally {
        if (unlinkBtn) { unlinkBtn.disabled = false; unlinkBtn.innerHTML = 'Unlink'; }
    }
}

// --- Bulk link many notes to one patient (B3 / FEAT-002) ---
// Selection mode shows a checkbox on each note card; a sticky bar hosts the patient
// picker + confirm. On confirm, POST /patients/{id}/link-notes {noteIds}. Counts only —
// never patientLabel / note text in logs or toasts (PHI).

/** Enable/disable the Link button: needs >=1 ticked AND a patient chosen. */
function updateBulkLinkBar() {
    const count = bulkSelectedIds.size;
    const countEl = document.getElementById('bulkLinkCount');
    if (countEl) countEl.textContent = count + ' selected';
    const linkBtn = document.getElementById('bulkLinkBtn');
    const pid = bulkPatientPicker ? bulkPatientPicker.getPatientId() : null;
    if (linkBtn) linkBtn.disabled = !(count >= 1 && pid);
}

/** Enter/exit the bulk-link selection mode. */
export function toggleBulkLinkMode() {
    bulkSelectMode = !bulkSelectMode;
    const bar = document.getElementById('bulkLinkBar');
    const toggleBtn = document.getElementById('bulkLinkToggleBtn');
    if (bar) bar.classList.toggle('d-none', !bulkSelectMode);
    if (toggleBtn) toggleBtn.classList.toggle('active', bulkSelectMode);

    if (bulkSelectMode) {
        bulkSelectedIds.clear();
        const host = document.getElementById('bulkPatientPicker');
        if (host && !bulkPatientPicker) {
            bulkPatientPicker = attachPatientPicker(host, { onChange: () => updateBulkLinkBar() });
        } else if (bulkPatientPicker) {
            bulkPatientPicker.reset();
        }
        const status = document.getElementById('bulkLinkStatus');
        if (status) { status.className = 'small ms-1'; status.textContent = ''; }
        updateBulkLinkBar();
    } else {
        bulkSelectedIds.clear();
        if (bulkPatientPicker) bulkPatientPicker.reset();
    }
    filterNotesLocally(); // re-render cards with/without checkboxes
}

/** Cancel button on the action bar — same as toggling off. */
export function cancelBulkLinkMode() {
    if (bulkSelectMode) toggleBulkLinkMode();
}

/** Tick/untick a note card in selection mode. */
export function toggleNoteSelect(noteId) {
    if (!bulkSelectMode) return;
    if (bulkSelectedIds.has(noteId)) bulkSelectedIds.delete(noteId);
    else bulkSelectedIds.add(noteId);
    const cb = document.querySelector('.form-check-input[data-action="toggle-note-select"][data-id="' + noteId + '"]');
    if (cb) cb.checked = bulkSelectedIds.has(noteId);
    updateBulkLinkBar();
}

/** Confirm + POST the ticked notes to the chosen patient. */
export async function bulkLinkConfirm() {
    const patientId = bulkPatientPicker ? bulkPatientPicker.getPatientId() : null;
    const noteIds = Array.from(bulkSelectedIds);
    const status = document.getElementById('bulkLinkStatus');
    const linkBtn = document.getElementById('bulkLinkBtn');
    if (!patientId || !isValidUuid(patientId) || noteIds.length === 0) return;

    const picked = document.querySelector('#bulkPatientPicker [data-patient-selected]');
    const label = picked ? picked.textContent.replace(/\u00d7\s*$/, '').trim() : 'this patient';
    const ok = await showConfirmDialog(
        'Link notes',
        'Link these ' + noteIds.length + ' notes to patient "' + escapeHtml(label) + '"? This groups them into one patient history.',
        'Link'
    );
    if (!ok) return;

    if (linkBtn) { linkBtn.disabled = true; linkBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Linking...'; }
    if (status) { status.className = 'small ms-1 text-muted'; status.textContent = ''; }
    try {
        const result = await bulkLinkNotes(patientId, noteIds);
        const linkedCount = (result.linked || []).length;
        const failedCount = (result.failed || []).length;
        showToast('Linked ' + linkedCount + ' notes', 'success');
        if (failedCount > 0) showToast(failedCount + ' could not be linked', 'warning');
        logger.info('NOTES', 'Bulk-linked notes', { patientId: patientId, linked: linkedCount, failed: failedCount });
        // Exit selection mode, reset picker + ticked set, then refresh the list.
        toggleBulkLinkMode();
        loadNotes(true);
    } catch (err) {
        if (status) { status.className = 'small ms-1 text-danger'; status.textContent = 'Could not link notes. Please try again.'; }
        if (linkBtn) { linkBtn.disabled = false; linkBtn.innerHTML = 'Link'; }
    }
}

export async function copyNote() {
    // If editing with unsaved changes, copy the live textarea; else the saved content.
    const editTa = document.getElementById('noteEditTextarea');
    const source = (editTa && editTa.offsetParent !== null) ? editTa.value : state.currentNoteContent;
    const cleanText = stripNoteForCopy(source);
    try {
        await navigator.clipboard.writeText(cleanText);
        const btn = document.getElementById('copyBtn');
        btn.textContent = '✓ Copied!';
        btn.classList.add('copied');
        setTimeout(() => { btn.textContent = '📋 Copy to Clipboard'; btn.classList.remove('copied'); }, 2000);
    } catch (err) {
        const ta = document.createElement('textarea');
        ta.value = cleanText;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
    }
}

// ============================================================================
// NOTE AUDIO PLAYBACK & TRANSCRIPT ON-DEMAND
// ============================================================================


/**
 * Try to find local audio blob in IndexedDB for a given noteId.
 * Sessions store `uploadedNoteId` after successful upload.
 * Returns a Blob or null.
 */
export async function findLocalAudioForNote(noteId) {
    try {
        const sessions = await SessionStore.getAll();
        for (const session of sessions) {
            if (session.uploadedNoteId === noteId && session.segments && session.segments.length > 0) {
                const mimeType = session.mimeType || 'audio/webm';
                return new Blob(session.segments, { type: mimeType });
            }
        }
    } catch (err) {
        // IndexedDB unavailable or error — fall through to S3
    }
    return null;
}

/**
 * Format seconds as m:ss or h:mm:ss.
 */
export function formatAudioTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    var s = Math.floor(seconds);
    var h = Math.floor(s / 3600);
    var m = Math.floor((s % 3600) / 60);
    var sec = s % 60;
    if (h > 0) return h + ':' + String(m).padStart(2, '0') + ':' + String(sec).padStart(2, '0');
    return m + ':' + String(sec).padStart(2, '0');
}

/**
 * Render the mini audio player widget inside a container element.
 * Returns the container innerHTML was set on.
 */
export function renderAudioPlayerWidget(container) {
    container.innerHTML = `
        <div class="audio-mini-player">
            <button class="audio-player-btn audio-play-pause" id="audioPlayerPlayPause" type="button" aria-label="Play" title="Play">
                <span class="audio-icon-play">&#9654;</span>
                <span class="audio-icon-pause hidden">&#10074;&#10074;</span>
                <span class="audio-icon-loading hidden"><span class="spinner-border spinner-border-sm" role="status"></span></span>
            </button>
            <span class="audio-player-time audio-current-time" id="audioPlayerCurrentTime">0:00</span>
            <input type="range" class="audio-player-progress" id="audioPlayerProgress" min="0" max="100" value="0" step="0.1" aria-label="Seek audio">
            <span class="audio-player-time audio-total-time" id="audioPlayerTotalTime">0:00</span>
        </div>`;
}

/**
 * Start progress animation loop for the audio player.
 */
export function startAudioProgressLoop() {
    function tick() {
        if (!noteAudioPlayer) return;
        var progress = document.getElementById('audioPlayerProgress');
        var currentTimeEl = document.getElementById('audioPlayerCurrentTime');
        if (progress && noteAudioPlayer.duration && isFinite(noteAudioPlayer.duration)) {
            var pct = (noteAudioPlayer.currentTime / noteAudioPlayer.duration) * 100;
            progress.value = pct;
            if (currentTimeEl) currentTimeEl.textContent = formatAudioTime(noteAudioPlayer.currentTime);
        }
        noteAudioAnimFrame = requestAnimationFrame(tick);
    }
    noteAudioAnimFrame = requestAnimationFrame(tick);
}

/**
 * Stop the progress animation loop.
 */
export function stopAudioProgressLoop() {
    if (noteAudioAnimFrame) {
        cancelAnimationFrame(noteAudioAnimFrame);
        noteAudioAnimFrame = null;
    }
}

/**
 * Update the play/pause button icons based on player state.
 */
export function updateAudioPlayerIcons(state) {
    var playIcon = document.querySelector('.audio-icon-play');
    var pauseIcon = document.querySelector('.audio-icon-pause');
    var loadingIcon = document.querySelector('.audio-icon-loading');
    if (!playIcon || !pauseIcon || !loadingIcon) return;
    playIcon.classList.toggle('hidden', state !== 'paused');
    pauseIcon.classList.toggle('hidden', state !== 'playing');
    loadingIcon.classList.toggle('hidden', state !== 'loading');
    var btn = document.getElementById('audioPlayerPlayPause');
    if (btn) {
        btn.setAttribute('aria-label', state === 'playing' ? 'Pause' : 'Play');
        btn.setAttribute('title', state === 'playing' ? 'Pause' : 'Play');
    }
}

/**
 * Play/pause audio for a note. First checks IndexedDB for local blob,
 * then falls back to presigned S3 URL via GET /notes/{noteId}/audio-url.
 *
 * When called from the modal (buttonEl is inside modalAudioSection), renders
 * a full mini player. When called from note cards, uses a compact toggle.
 */
export async function playNoteAudio(noteId, buttonEl) {
    if (!isValidUuid(noteId)) {
        logger.warn('NOTES', 'playNoteAudio called with invalid noteId');
        return;
    }

    var isModalPlayer = buttonEl && buttonEl.closest && buttonEl.closest('#modalAudioSection');

    // If already playing this note, toggle pause/play
    if (noteAudioPlayer && noteAudioPlayer._noteId === noteId) {
        if (noteAudioPlayer.paused) {
            noteAudioPlayer.play();
            if (isModalPlayer) {
                updateAudioPlayerIcons('playing');
                startAudioProgressLoop();
            } else {
                buttonEl.innerHTML = '&#9724; Stop';
                buttonEl.classList.add('playing');
            }
        } else {
            if (isModalPlayer) {
                noteAudioPlayer.pause();
                updateAudioPlayerIcons('paused');
                stopAudioProgressLoop();
            } else {
                stopNoteAudio();
            }
        }
        return;
    }

    // Stop any existing playback
    stopNoteAudio();

    // Show loading state
    if (isModalPlayer) {
        var section = document.getElementById('modalAudioSection');
        renderAudioPlayerWidget(section);
        updateAudioPlayerIcons('loading');
    } else {
        var originalContent = buttonEl.innerHTML;
        buttonEl.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span>';
        buttonEl.disabled = true;
    }

    try {
        // 1. Check IndexedDB SessionStore for local audio (recent recordings)
        var localBlob = await findLocalAudioForNote(noteId);

        // 2. Check AudioCache (previously downloaded from S3)
        if (!localBlob) {
            var cached = await AudioCache.get(noteId);
            if (cached && cached.blob) localBlob = cached.blob;
        }

        if (localBlob) {
            noteAudioObjectUrl = URL.createObjectURL(localBlob);
            noteAudioPlayer = new Audio(noteAudioObjectUrl);
        } else {
            // 3. Fetch presigned URL from backend, download blob, cache locally
            var res = await apiFetchWithRefresh('/notes/' + noteId + '/audio-url');
            if (!res.ok) {
                var errData = await res.json().catch(function() { return {}; });
                throw new Error(errData.error || 'Failed to get audio URL');
            }
            var data = await res.json();
            if (!data.audioUrl) {
                if (isModalPlayer) {
                    document.getElementById('modalAudioSection').innerHTML = '<span class="text-muted small">No audio available</span>';
                } else {
                    buttonEl.innerHTML = originalContent;
                    buttonEl.disabled = false;
                    buttonEl.title = 'No audio available';
                }
                return;
            }
            // Download blob locally — never re-fetch from S3 on repeat plays
            var fetchRes = await fetch(data.audioUrl);
            if (!fetchRes.ok) throw new Error('Audio download failed: ' + fetchRes.status);
            var downloadedBlob = await fetchRes.blob();
            // Cache for future plays (fire-and-forget)
            AudioCache.put(noteId, downloadedBlob);
            noteAudioObjectUrl = URL.createObjectURL(downloadedBlob);
            noteAudioPlayer = new Audio(noteAudioObjectUrl);
            noteAudioPlayer._audioUrl = data.audioUrl;
        }

        noteAudioPlayer._noteId = noteId;
        noteAudioPlayer.preload = 'metadata';

        if (isModalPlayer) {
            // Wire up the mini player controls
            noteAudioPlayer.addEventListener('loadedmetadata', function() {
                var totalEl = document.getElementById('audioPlayerTotalTime');
                if (totalEl) totalEl.textContent = formatAudioTime(noteAudioPlayer.duration);
                var progress = document.getElementById('audioPlayerProgress');
                if (progress) progress.max = 100;
            });

            noteAudioPlayer.addEventListener('ended', function() {
                updateAudioPlayerIcons('paused');
                stopAudioProgressLoop();
                var progress = document.getElementById('audioPlayerProgress');
                if (progress) progress.value = 100;
                var currentTimeEl = document.getElementById('audioPlayerCurrentTime');
                if (currentTimeEl) currentTimeEl.textContent = formatAudioTime(noteAudioPlayer.duration);
            });

            noteAudioPlayer.addEventListener('error', function() {
                stopAudioProgressLoop();
                noteAudioPlayer = null;
                // Format not supported — try AudioContext decode on the local blob
                attemptAudioContextFallback(noteId, true, null);
                logger.warn('NOTES', 'Audio playback error, attempting AudioContext fallback', { noteId: noteId });
            });

            // Seek handler on progress bar
            var progressBar = document.getElementById('audioPlayerProgress');
            if (progressBar) {
                progressBar.addEventListener('input', function() {
                    if (noteAudioPlayer && noteAudioPlayer.duration && isFinite(noteAudioPlayer.duration)) {
                        noteAudioPlayer.currentTime = (parseFloat(this.value) / 100) * noteAudioPlayer.duration;
                        var currentTimeEl = document.getElementById('audioPlayerCurrentTime');
                        if (currentTimeEl) currentTimeEl.textContent = formatAudioTime(noteAudioPlayer.currentTime);
                    }
                });
            }

            // Play/pause button handler
            var playPauseBtn = document.getElementById('audioPlayerPlayPause');
            if (playPauseBtn) {
                playPauseBtn.addEventListener('click', function() {
                    if (!noteAudioPlayer) return;
                    if (noteAudioPlayer.paused) {
                        noteAudioPlayer.play();
                        updateAudioPlayerIcons('playing');
                        startAudioProgressLoop();
                    } else {
                        noteAudioPlayer.pause();
                        updateAudioPlayerIcons('paused');
                        stopAudioProgressLoop();
                    }
                });
            }

            // Start playback
            await noteAudioPlayer.play();
            updateAudioPlayerIcons('playing');
            startAudioProgressLoop();
        } else {
            // Simple card-level playback
            noteAudioPlayer.addEventListener('ended', function() {
                stopNoteAudio();
                buttonEl.innerHTML = '&#128266; Play';
                buttonEl.classList.remove('playing');
                buttonEl.disabled = false;
            });

            noteAudioPlayer.addEventListener('error', function() {
                stopNoteAudio();
                // Format not supported — try AudioContext decode on the local blob
                attemptAudioContextFallback(noteId, false, buttonEl);
                logger.warn('NOTES', 'Audio playback error, attempting AudioContext fallback', { noteId: noteId });
            });

            await noteAudioPlayer.play();
            buttonEl.innerHTML = '&#9724; Stop';
            buttonEl.classList.add('playing');
            buttonEl.disabled = false;
        }
    } catch (err) {
        stopNoteAudio();
        if (isModalPlayer) {
            document.getElementById('modalAudioSection').innerHTML = '<span class="text-danger small">Failed to play audio</span>';
        } else {
            buttonEl.innerHTML = originalContent;
            buttonEl.disabled = false;
        }
        logger.warn('NOTES', 'Failed to play note audio', { noteId: noteId, error: err.message });
    }
}

/**
 * Stop audio playback and clean up resources.
 */
export function stopNoteAudio() {
    stopAudioProgressLoop();
    if (noteAudioPlayer) {
        noteAudioPlayer.pause();
        noteAudioPlayer.src = '';
        noteAudioPlayer = null;
    }
    if (noteAudioObjectUrl) {
        URL.revokeObjectURL(noteAudioObjectUrl);
        noteAudioObjectUrl = null;
    }
    // Reset any play buttons in the DOM
    var playingBtns = document.querySelectorAll('.note-play-btn.playing');
    playingBtns.forEach(function(btn) {
        btn.innerHTML = '&#128266; Play';
        btn.classList.remove('playing');
    });
}

/**
 * Fallback: decode cached blob via AudioContext when <audio> element can't play the format.
 * The blob is already in AudioCache (downloaded earlier). No network calls needed.
 */
export async function attemptAudioContextFallback(noteId, isModal, buttonEl) {
    try {
        // Get blob from AudioCache or SessionStore
        var blob = null;
        var cached = await AudioCache.get(noteId);
        if (cached && cached.blob) {
            blob = cached.blob;
        } else {
            blob = await findLocalAudioForNote(noteId);
        }
        if (!blob) throw new Error('No local blob found');

        var arrayBuf = await blob.arrayBuffer();
        var audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        var audioBuffer = await audioCtx.decodeAudioData(arrayBuf);
        var source = audioCtx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioCtx.destination);
        source.start(0);

        // Clean up object URL from failed attempt
        if (noteAudioObjectUrl) {
            URL.revokeObjectURL(noteAudioObjectUrl);
            noteAudioObjectUrl = null;
        }
        noteAudioPlayer = null;

        if (isModal) {
            var totalEl = document.getElementById('audioPlayerTotalTime');
            if (totalEl) totalEl.textContent = formatAudioTime(audioBuffer.duration);
            updateAudioPlayerIcons('playing');
            source.onended = function() {
                updateAudioPlayerIcons('paused');
                stopAudioProgressLoop();
                audioCtx.close();
            };
        } else if (buttonEl) {
            buttonEl.innerHTML = '&#9724; Stop';
            buttonEl.classList.add('playing');
            buttonEl.disabled = false;
            source.onended = function() {
                buttonEl.innerHTML = '&#128266; Play';
                buttonEl.classList.remove('playing');
                audioCtx.close();
            };
        }
        logger.info('NOTES', 'AudioContext decode fallback succeeded', { noteId: noteId });
    } catch (err) {
        // All methods failed — show error with download option
        if (noteAudioObjectUrl) {
            URL.revokeObjectURL(noteAudioObjectUrl);
            noteAudioObjectUrl = null;
        }
        noteAudioPlayer = null;

        var errorHtml = '<div class="audio-fallback-download text-center py-2">' +
            '<p class="text-warning small mb-2">Cannot play this audio format on your browser</p>' +
            '</div>';

        if (isModal) {
            document.getElementById('modalAudioSection').innerHTML = errorHtml;
        } else if (buttonEl) {
            buttonEl.innerHTML = '&#128266; Play';
            buttonEl.classList.remove('playing');
            buttonEl.disabled = false;
            var wrapper = buttonEl.parentElement;
            if (wrapper) {
                var existingFallback = wrapper.querySelector('.audio-fallback-download');
                if (existingFallback) existingFallback.remove();
                buttonEl.insertAdjacentHTML('afterend', errorHtml);
            }
        }
        logger.warn('NOTES', 'All audio playback methods failed', { noteId: noteId, error: err.message });
    }
}

/**
 * Fetch and display transcript for a note on demand.
 * Called from "Show Transcript" button in the note modal.
 */
export async function showNoteTranscript(noteId) {
    const container = document.getElementById('modalTranscriptContainer');
    const btn = document.getElementById('showTranscriptBtn');
    if (!container || !btn) return;

    if (!isValidUuid(noteId)) {
        container.innerHTML = '<div class="text-danger py-2 small">Invalid note ID — cannot load transcript.</div>';
        container.classList.add('show');
        return;
    }

    // If transcript already loaded and visible, toggle hide
    if (container.classList.contains('show') && container.dataset.noteId === noteId) {
        container.classList.remove('show');
        btn.innerHTML = '&#128196; Show Transcript';
        return;
    }

    // Show loading state
    btn.disabled = true;
    btn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status"></span> Loading...';
    container.innerHTML = '<div class="text-center text-muted py-3"><span class="spinner-border spinner-border-sm" role="status"></span> Loading transcript...</div>';
    container.classList.add('show');
    container.dataset.noteId = noteId;

    try {
        const res = await apiFetchWithRefresh('/notes/' + noteId + '/transcript');
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.error || 'Failed to load transcript');
        }
        const data = await res.json();

        if (!data.transcript) {
            container.innerHTML = '<div class="text-muted py-2 small">No transcript available for this note.</div>';
        } else if (data.segments && data.segments.length > 0) {
            // Render structured segments as conversation layout
            container.innerHTML = renderTranscriptSegments(data.segments);
        } else {
            // Fallback: plain text for old notes without segments
            container.innerHTML = '<div class="transcript-text">' + escapeHtml(data.transcript).replace(/\n/g, '<br>') + '</div>';
        }
        btn.innerHTML = '&#128196; Hide Transcript';
        btn.disabled = false;
    } catch (err) {
        container.innerHTML = '<div class="text-danger py-2 small">Error loading transcript: ' + escapeHtml(err.message) + '</div>';
        btn.innerHTML = '&#128196; Show Transcript';
        btn.disabled = false;
        logger.warn('NOTES', 'Failed to load transcript', { noteId: noteId, error: err.message });
    }
}

function renderTranscriptSegments(segments) {
    var html = '';
    for (var i = 0; i < segments.length; i++) {
        var seg = segments[i];
        var speaker = seg.speaker || 'Unknown';
        var isDoctor = speaker.toLowerCase() === 'doctor';
        var bgClass = isDoctor ? 'bg-primary bg-opacity-10' : 'bg-success bg-opacity-10';
        var labelClass = isDoctor ? 'text-primary' : 'text-success';
        var timestamp = formatSegmentTime(seg.startTime);

        html += '<div class="transcript-segment d-flex align-items-start p-2 mb-2 rounded ' + bgClass + '">'
            + '<div class="flex-grow-1">'
            + '<strong class="small ' + labelClass + '">' + escapeHtml(speaker) + '</strong>'
            + '<div class="small">' + escapeHtml(seg.text) + '</div>'
            + '</div>'
            + '<span class="text-muted small ms-2 text-nowrap">' + timestamp + '</span>'
            + '</div>';
    }
    return html;
}

function formatSegmentTime(seconds) {
    if (seconds == null || isNaN(seconds)) return '';
    var totalSec = Math.floor(seconds);
    var min = Math.floor(totalSec / 60);
    var sec = totalSec % 60;
    return (min < 10 ? '0' : '') + min + ':' + (sec < 10 ? '0' : '') + sec;
}

// ============================================================================